import Dropdown from '@Components/Dropdown';
import SbTextField from '@Components/Textfield';
import Title from '@Components/Title';
import React, { Checkbox, Grid, Typography } from '@mui/material';
// import GppGoodIcon from '@mui/icons-material/GppGood';
// import Inventory2Icon from '@mui/icons-material/Inventory2';
import { RootStateType } from '@Store';
import { useSelector } from 'react-redux';
import { SHIPMENT_MODES, SHIPMENT_UNITS, defaultBoxState } from '../../CreateShipment/constants';
import { getBoxStateObj, getUserDetailsStateObj } from '@Services/getShipmentPayload';

import { useContext, useEffect, useMemo } from 'react';
import CreateShipmentContext from '../../../context/CreateShipment';
import { createShipmentContextType } from '../../../types/shipment';
import ShipmentDetailsBoxWise from '../../CreateShipment/ShipmentDetails';
import useScroll from '../useScroll';
import { SHIPMENT_TYPE } from '@Constants/Shipment';
import { isPurposeOfShipmentRequired } from '../../../utils/getSubdomain';

const SHIPMENT_TYPE_LITS = [
  { value: 'parcel', display: 'Parcel' },
  { value: 'document', display: 'Document' },
];

function ShipmentDetails() {
  const { shipmentDetailsRef } = useScroll();
  const {
    setBoxes,
    setRemoveFirstBoxDeleteOption,
    packageData,
    shipmentType,
    setShipmentType,
    setPackageData,
    setSelectedBox,
    setSelectedProduct,
    extraDetails,
    setExtraDetails,
    setConsignee,
  } = useContext(CreateShipmentContext) as createShipmentContextType;
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);

  useEffect(() => {
    if (shipmentById) {
      const { _packageId, shipmentMode, extraDetails, userReferenceNumber } = shipmentById;
      const { box, insurance, packaging, isDocument, unitOfMeasurement, currency, invoiceNumber } = _packageId;
      const boxData = getBoxStateObj(box || []);
      setBoxes(boxData);
      setRemoveFirstBoxDeleteOption(true);
      const anyData = shipmentById as any;
      const incoterm =
        anyData?.incoterm || (_packageId as any)?.incoterm || (extraDetails?.other as any)?.incoterm || 'FOB';
      const freight = anyData?.freight ?? (_packageId as any)?.freight ?? (extraDetails?.other as any)?.freight ?? 0;
      const freightInsurance =
        anyData?.freightInsurance ??
        (_packageId as any)?.freightInsurance ??
        (extraDetails?.other as any)?.freightInsurance ??
        0;
      const iossNumber =
        anyData?.iossNumber || (_packageId as any)?.iossNumber || (extraDetails?.other as any)?.iossNumber || '';
      const recipientVatTaxId =
        (shipmentById?._consigneeId as any)?.vat ||
        (shipmentById as any)?.consignee?.vat ||
        anyData?.consignee?.vat ||
        anyData?._consigneeId?.vat ||
        anyData?.recipientVatTaxId ||
        (_packageId as any)?.recipientVatTaxId ||
        (extraDetails?.other as any)?.recipientVatTaxId ||
        '';
      const iorEoriNumber =
        anyData?.iorEoriNumber ||
        (_packageId as any)?.iorEoriNumber ||
        extraDetails?.other?.ior ||
        (extraDetails?.other as any)?.iorEoriNumber ||
        '';

      setShipmentType({
        ...shipmentType,
        type: isDocument ? 'document' : 'parcel',
        shipmentMode,
        currency,
        unit: unitOfMeasurement === 'kg-cm' ? 'kg-cm' : 'lb-in',
        invoiceNumber: invoiceNumber || '',
        referenceNumber: userReferenceNumber,
        dutyCharge:
          (shipmentById as any)?.dutyCharge ||
          (shipmentById as any)?._packageId?.dutyCharge ||
          (shipmentById as any)?.package?.dutyCharge ||
          '',
        incoterm,
        freight,
        freightInsurance,
        iossNumber,
        recipientVatTaxId,
        iorEoriNumber,
      });
      setPackageData({
        ...packageData,
        unitOfMeasurement,
        insurance,
        packaging,
        userReferenceNumber: userReferenceNumber,
        incoterm,
        freight,
        freightInsurance,
        iossNumber,
        recipientVatTaxId,
        iorEoriNumber,
      });
      if (boxData.length) {
        setSelectedBox(boxData[0]);
      } else {
        setSelectedBox({ ...defaultBoxState });
      }
      setSelectedProduct(null);
      if (shipmentById?._consigneeId && setConsignee) {
        setConsignee(getUserDetailsStateObj(shipmentById._consigneeId));
      }
      if (extraDetails?.other) setExtraDetails({ ...extraDetails?.other });
    }
  }, [shipmentById]);

  const { currencyList } = useSelector((state: RootStateType) => state?.global);
  const isPurposeRequired = isPurposeOfShipmentRequired(shipmentType);

  return (
    <>
      <Grid container mb={2} ref={shipmentDetailsRef}>
        <Title title="Shipment Details" removeBorder removePadding />
      </Grid>
      <Grid container gap={3} className="p-4 mb-3 border border-solid border-light rounded">
        <Grid container item columnSpacing={2} rowSpacing={3}>
          <Grid item xs={12} md={3} lg={3.5} xl={2}>
            <Dropdown
              id="ShipmentType"
              required
              label="Shipment Type"
              name="name"
              options={SHIPMENT_TYPE_LITS}
              value={shipmentType.type ? [shipmentType.type] : []}
              onChange={([value]) => setShipmentType({ ...shipmentType, type: value })}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3.5} xl={2}>
            <Dropdown
              id="ShipmentMode"
              required
              label="Shipment Mode"
              name="shipmentMode"
              options={SHIPMENT_MODES}
              value={shipmentType.shipmentMode ? [shipmentType.shipmentMode] : []}
              onChange={([value]) => setShipmentType({ ...shipmentType, shipmentMode: value })}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3.5} xl={2}>
            <Dropdown
              id="preferredUnit"
              required
              label="Preferred Unit"
              name="name"
              options={SHIPMENT_UNITS}
              value={packageData.unitOfMeasurement ? [packageData.unitOfMeasurement] : []}
              onChange={([value]) => setPackageData({ ...packageData, unitOfMeasurement: value })}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3.5} xl={2}>
            <Dropdown
              id="Currency"
              required
              label="Currency"
              name="name"
              options={currencyList}
              value={shipmentType.currency ? [shipmentType.currency] : []}
              onChange={([value]) => setShipmentType({ ...shipmentType, currency: value })}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3.5} xl={2}>
            <SbTextField
              label="No Of Boxes"
              name="name"
              value={shipmentById?._packageId?.box?.length || ''}
              disabled
              //   onChange={handleChange}
              //   error={errors?.name}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3.5} xl={2}>
            <SbTextField
              label="Reference No."
              name="name"
              // disabled
              value={shipmentType?.referenceNumber}
              onChange={e => setShipmentType({ ...shipmentType, referenceNumber: e.target.value })}
              //   error={errors?.name}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3.5} xl={2}>
            <SbTextField
              label="Invoice No."
              name="name"
              // disabled
              value={shipmentType?.invoiceNumber || ''}
              onChange={e => setShipmentType({ ...shipmentType, invoiceNumber: e.target.value })}
              //   error={errors?.name}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3.5} xl={2}>
            <Dropdown
              id="purposeOfShipment"
              required={isPurposeRequired}
              label="Purpose Of Shipment"
              name="name"
              options={SHIPMENT_TYPE}
              value={extraDetails.purposeOfShipment ? [extraDetails.purposeOfShipment] : []}
              onChange={([value]) => setExtraDetails({ ...extraDetails, purposeOfShipment: value })}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={3.5} xl={2}>
            <SbTextField
              label="IOR/EORI No."
              name="name"
              value={extraDetails.ior}
              onChange={value => setExtraDetails({ ...extraDetails, ior: value.target.value })}
              //   error={errors?.name}
            />
          </Grid>
        </Grid>
        <Grid container alignItems="center" className="pt-3" spacing={6}>
          <Insurance
            value={packageData.insurance}
            onChange={value => setPackageData({ ...packageData, insurance: value })}
          />
          <Packaging
            value={packageData.packaging}
            onChange={value => setPackageData({ ...packageData, packaging: value })}
          />
        </Grid>
        <ShipmentDetailsBoxWise removeShipmentInfo showViewMoreAction />
      </Grid>
    </>
  );
}

// const IconStyles = { color: 'var(--mui-palette-primary-main)', height: '24px', width: '24px' };
interface InsuranceProps {
  value: boolean;
  onChange: (value: boolean) => void;
}
function Insurance(props: InsuranceProps) {
  const shipmentSetting = useSelector((state: RootStateType) => state.orgSetup.shipmentSetting);
  const disable = useMemo(() => !shipmentSetting?.insuranceCharge?.charge, [shipmentSetting?.insuranceCharge?.charge]);
  return (
    <Grid container item md="auto" alignItems={'center'} className={`${disable ? 'disabled-form' : 'cursor-pointer'}`}>
      <Checkbox
        name="insurance"
        id="insurance"
        className="mr-1/2"
        checked={!!props.value}
        onChange={() => props.onChange(!props.value)}
        // onChange={handleChange}
        inputProps={{ 'aria-label': 'controlled' }}
        disabled={disable}
      />
      {/* <GppGoodIcon sx={IconStyles} /> */}
      <img src="/images/Boxes/Insurance.svg" alt="logistics" className="mr-1/2 sm-text" />
      <Typography
        component={'label'}
        htmlFor="insurance"
        className="md-text font-medium sb-text-info ml-1"
        variant="caption"
      >
        Insure Your Shipment
      </Typography>
    </Grid>
  );
}

interface PackagingProps {
  value: boolean;
  onChange: (value: boolean) => void;
}

function Packaging(props: PackagingProps) {
  const shipmentSetting = useSelector((state: RootStateType) => state.orgSetup.shipmentSetting);
  const disable = useMemo(() => !shipmentSetting?.packagingCharge?.charge, [shipmentSetting?.packagingCharge?.charge]);
  return (
    <Grid container item md="auto" alignItems={'center'} className={`${disable ? 'disabled-form' : 'cursor-pointer'}`}>
      <Checkbox
        name="packaging"
        id="packaging"
        className="mr-1/2"
        checked={!!props.value}
        onChange={() => props.onChange(!props.value)}
        inputProps={{ 'aria-label': 'controlled' }}
        disabled={disable}
      />
      {/* <Box sx={IconStyles} /> */}
      <img src="/images/Boxes/Box.svg" alt="logistics" className="mr-1/2 sm-text" />
      <Typography
        component={'label'}
        htmlFor="packaging"
        className="md-text font-medium sb-text-info ml-1"
        variant="caption"
      >
        Add Packaging
      </Typography>
    </Grid>
  );
}

// function ContextWrapper() {
//   return (
//     <CreateShipmentWrapper>
//       <ShipmentDetails />
//     </CreateShipmentWrapper>
//   );
// }

export default ShipmentDetails;
